import { Component, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import type { HomeSection, WidgetDef, WidgetInstance } from '../../shared/types';
import { CORE_TOOL_ID, CORE_WIDGETS, WIDGET_SIZE_LABEL } from '../../shared/widgets';
import { TOOL_WIDGETS } from '../../tools/registry.widgets';
import type { WidgetProps } from '../lib/toolkit';
import { toolApi } from '../lib/api';
import { useMe } from '../lib/me';
import { navigate, Link } from '../lib/router';
import { newWidgetKey, saveWidgets } from '../lib/widgets';
import { coreWidget } from './CoreWidgets';
import { Icon } from './Icon';
import { Button, Loading, Modal, useToast } from './ui';
import { DragHandle, useSortable } from '../lib/sortable';

/** Same minimum card width as the tool cards on division pages, so pages flow alike. */
const MIN_COL = 260;
const GAP = 14;

const moduleCache = new Map<string, Promise<Record<string, ComponentType<WidgetProps>>>>();
function loadWidgets(toolId: string) {
  if (!moduleCache.has(toolId)) {
    const loader = TOOL_WIDGETS[toolId];
    moduleCache.set(toolId, loader ? loader().then((m) => m.widgets) : Promise.resolve({}));
  }
  return moduleCache.get(toolId)!;
}

class WidgetBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    return this.state.error ? <div className="widget-error"><Icon name="warn" size={16} /> This widget failed to load.</div> : this.props.children;
  }
}

interface Source { id: string; name: string; icon: string; widgets: WidgetDef[]; link: string | null }
interface Placed { inst: WidgetInstance; def: WidgetDef; source: Source }

function WidgetBody({ placed, onConfig }: { placed: Placed; onConfig: (c: Record<string, unknown>) => Promise<void> }) {
  const { me, can } = useMe();
  const isCore = placed.source.id === CORE_TOOL_ID;
  const [Comp, setComp] = useState<ComponentType<WidgetProps> | null | undefined>(undefined);
  const api = useMemo(() => toolApi(placed.source.id), [placed.source.id]);
  useEffect(() => {
    if (isCore) return;
    let live = true;
    loadWidgets(placed.source.id).then((w) => live && setComp(() => w[placed.def.id] ?? null)).catch(() => live && setComp(null));
    return () => { live = false; };
  }, [placed.source.id, placed.def.id, isCore]);
  if (isCore) return <WidgetBoundary>{coreWidget(placed.def.id)?.render() ?? null}</WidgetBoundary>;
  const tool = me?.nav.flatMap((s) => s.tools).find((t) => t.id === placed.source.id);
  if (Comp === undefined) return <Loading />;
  if (Comp === null || !me || !tool) return <div className="widget-error">Widget unavailable.</div>;
  return (
    <WidgetBoundary>
      <Comp tool={tool} me={me} can={can} api={api} config={placed.inst.config ?? {}} setConfig={onConfig}
        go={(p) => navigate(`/t/${tool.id}${p ? `/${p}` : ''}`)} />
    </WidgetBoundary>
  );
}

/** How many columns fit the board's width (re-measured as the window resizes). */
function useColumns() {
  const ref = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(4);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setCols(Math.max(1, Math.floor((el.clientWidth + GAP) / (MIN_COL + GAP))));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, cols };
}

/**
 * The home page board. Widgets sit in sections (headings the member names); every card has the
 * same height, is 1–4 columns wide and shrinks to fit narrow screens. In Customize, drag a widget
 * by its grip (cards move live; drop it on another section's widgets or empty drop zone to move
 * it there) or use its section menu; sections can be added, renamed, moved and removed.
 */
export function WidgetBoard() {
  const { me, refresh, tx } = useMe();
  const toast = useToast();
  const { ref, cols } = useColumns();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [list, setList] = useState<WidgetInstance[]>(me?.widgets ?? []);
  const [secs, setSecs] = useState<HomeSection[]>(me?.homeSections ?? []);
  useEffect(() => { setList(me?.widgets ?? []); }, [me?.widgets]);
  useEffect(() => { setSecs(me?.homeSections ?? []); }, [me?.homeSections]);

  const sources = useMemo(() => {
    const m = new Map<string, Source>();
    m.set(CORE_TOOL_ID, { id: CORE_TOOL_ID, name: 'Home', icon: 'home', widgets: me?.coreWidgets ?? CORE_WIDGETS, link: null });
    for (const t of (me?.nav ?? []).flatMap((s) => s.tools)) {
      if (t.widgets?.length) m.set(t.id, { id: t.id, name: t.name, icon: t.icon ?? 'tool', widgets: t.widgets, link: `/t/${t.id}` });
    }
    return m;
  }, [me?.nav, me?.coreWidgets]);
  const placed: Placed[] = list.flatMap((inst) => {
    const source = sources.get(inst.toolId);
    const def = source?.widgets.find((w) => w.id === inst.widgetId);
    return source && def ? [{ inst, def, source }] : [];
  });

  /** Always at least one section; a lone unnamed one shows no heading (the home page as it always was). */
  const sections: HomeSection[] = secs.length ? secs : [{ id: 'main', name: '' }];
  const sectionOf = (w: WidgetInstance) => (w.section && sections.some((x) => x.id === w.section) ? w.section : sections[0].id);
  const showHeads = sections.length > 1 || !!sections[0].name || editing;

  const persist = async (next: WidgetInstance[], nextSecs?: HomeSection[], msg?: string) => {
    const prev = list, prevSecs = secs;
    setList(next);
    if (nextSecs) setSecs(nextSecs);
    try {
      await saveWidgets(next, nextSecs);
      await refresh();
      if (msg) toast(msg);
    } catch (e) { setList(prev); setSecs(prevSecs); toast((e as Error).message, 'danger'); }
  };
  /** The section list to save: the implicit unnamed one becomes real once the member organises sections. */
  const realSections = () => (secs.length ? secs : sections);

  /** Drop a widget before another one, or at the end of a section. */
  const drop = (key: string, section: string, beforeKey: string | null) => {
    const moving = list.find((w) => w.key === key);
    if (!moving || key === beforeKey) return;
    const fromSection = sectionOf(moving);
    // Pin every widget to its section so the order stays put whatever the default is.
    const rest = list.filter((w) => w.key !== key).map((w) => ({ ...w, section: sectionOf(w) }));
    let at = beforeKey ? rest.findIndex((w) => w.key === beforeKey) : -1;
    if (at < 0) { const last = rest.map((w) => w.section).lastIndexOf(section); at = last >= 0 ? last + 1 : rest.length; }
    rest.splice(at, 0, { ...moving, section });
    void persist(rest, realSections(), fromSection !== section ? `Moved to ${sections.find((x) => x.id === section)?.name || 'the top section'}` : undefined);
  };
  // Drag by the grip: cards move live while dragging (as before sections); hovering a widget in
  // another section, or an empty section's drop zone, moves the widget into that section.
  const sort = useSortable(list, (w) => w.key, setList, {
    adopt: (dragged, over) => (sectionOf(dragged) === sectionOf(over) ? dragged : { ...dragged, section: sectionOf(over) }),
    onForeign: (key, target) => {
      if (!target.startsWith('section:')) return;
      const section = target.slice(8);
      const moving = list.find((w) => w.key === key);
      if (!moving || sectionOf(moving) === section) return;
      const rest = list.filter((w) => w.key !== key);
      const last = rest.map(sectionOf).lastIndexOf(section);
      rest.splice(last >= 0 ? last + 1 : rest.length, 0, { ...moving, section });
      setList(rest);
    },
    onDrop: (next) => { void persist(next.map((w) => ({ ...w, section: sectionOf(w) })), realSections()); },
  });
  const moveSection = (i: number, d: -1 | 1) => {
    const next = [...sections];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    void persist(list.map((w) => ({ ...w, section: sectionOf(w) })), next);
  };
  const renameSection = (id: string) => {
    const c = sections.find((x) => x.id === id)!;
    const name = prompt('Section name (leave empty for no heading)', c.name);
    if (name === null || name.trim() === c.name) return;
    void persist(list, sections.map((x) => (x.id === id ? { ...x, name: name.trim().slice(0, 60) } : x)), 'Section renamed');
  };
  const addSection = () => {
    const name = prompt('New section name, e.g. Comms, Operations, My unit')?.trim();
    if (!name) return;
    const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30) || 'section';
    let id = base;
    for (let n = 2; sections.some((x) => x.id === id); n++) id = `${base}-${n}`;
    void persist(list.map((w) => ({ ...w, section: sectionOf(w) })), [...sections, { id, name: name.slice(0, 60) }], 'Section added');
  };
  const removeSection = (id: string) => {
    if (sections.length < 2) return;
    const into = sections.find((x) => x.id !== id)!;
    const count = list.filter((w) => sectionOf(w) === id).length;
    if (count && !confirm(`Remove this section? Its ${count} widget${count > 1 ? 's move' : ' moves'} to “${into.name || 'the top section'}”.`)) return;
    void persist(list.map((w) => ({ ...w, section: sectionOf(w) === id ? into.id : sectionOf(w) })), sections.filter((x) => x.id !== id), 'Section removed');
  };

  const sizeOf = (p: Placed) => p.inst.size ?? p.def.size;
  if (!me) return null;

  const card = (p: Placed) => {
    const icon = p.source.id === CORE_TOOL_ID ? coreWidget(p.def.id)?.icon ?? 'home' : p.source.icon;
    const title = p.source.link
      ? <Link to={p.source.link} className="widget-title">{p.def.title}<span className="muted">{p.source.name}</span></Link>
      : <span className="widget-title">{p.def.title}</span>;
    const span = Math.min(sizeOf(p), cols);
    const key = p.inst.key;
    const section = sectionOf(p.inst);
    return (
      <section key={key} {...(editing ? sort.item(key, `panel widget span-${span} organising`) : { className: `panel widget span-${span}` })}>
        <header className="panel-head">
          {editing && <DragHandle {...sort.handle(key, `Move ${p.def.title}`)} />}
          <h3 className="grow"><Icon name={icon} size={16} />{title}</h3>
          {editing && <button className="icon-btn" aria-label={`Remove ${p.def.title}`} title="Remove" onClick={() => persist(list.filter((x) => x.key !== key))}><Icon name="close" size={15} /></button>}
        </header>
        {editing && (
          <div className="widget-tools">
            {sections.length > 1 && (
              <select className="widget-size widget-move" value={section} aria-label={`Move ${p.def.title} to section`} onChange={(e) => drop(key, e.target.value, null)}>
                {sections.map((x) => <option key={x.id} value={x.id}>{x.name || 'Top section'}</option>)}
              </select>
            )}
            <select className="widget-size" value={sizeOf(p)} aria-label={`Width of ${p.def.title}`}
              onChange={(e) => persist(list.map((x) => (x.key === key ? { ...x, size: Number(e.target.value) as 1 | 2 | 3 | 4 } : x)))}>
              {[1, 2, 3, 4].map((w) => <option key={w} value={w}>{w === 4 ? 'Full width' : `${w} col${w > 1 ? 's' : ''}`}</option>)}
            </select>
          </div>
        )}
        <div className="panel-body">
          <WidgetBody placed={p} onConfig={(config) => persist(list.map((x) => (x.key === key ? { ...x, config } : x)))} />
        </div>
      </section>
    );
  };

  return (
    <section className="widgets-section">
      <div className="widgets-head">
        <div className="grow" />
        {editing && <Button icon="plus" onClick={() => setAdding(sections[0].id)}>{tx('Add widget')}</Button>}
        <Button variant={editing ? 'primary' : 'ghost'} icon={editing ? 'check' : 'grip'} onClick={() => setEditing(!editing)}>
          {editing ? tx('Done') : tx('Customize home')}
        </Button>
      </div>
      {editing && <p className="muted small widgets-hint">Drag widgets by their grip to reorder them or move them into another section (or use each widget’s section menu). Add sections to group widgets under headings. Changes save as you go.</p>}
      <div ref={ref} className="widget-sections">
        {placed.length === 0 && !editing && (
          <div className="widgets-empty">
            <Icon name="overview" size={22} />
            <div><strong>Your home page is empty</strong><span className="muted">Add widgets for Discord channels, events, favorites and live information from your tools.</span></div>
            <Button icon="plus" onClick={() => { setEditing(true); setAdding(sections[0].id); }}>Add widget</Button>
          </div>
        )}
        {sections.map((sec, i) => {
          const inSec = placed.filter((p) => sectionOf(p.inst) === sec.id);
          if (!editing && !inSec.length) return null;
          return (
            <section key={sec.id} className={`home-section ${editing ? 'organising' : ''}`}>
              {showHeads && (
                <div className="rules-cat-head home-section-head">
                  <h4 className="group-title">{sec.name || (editing ? <span className="muted">Top section (no heading)</span> : null)}</h4>
                  {editing && <span className="row-actions">
                    <button className="icon-btn" aria-label={`Add a widget to ${sec.name || 'this section'}`} title="Add widget here" onClick={() => setAdding(sec.id)}><Icon name="plus" size={14} /></button>
                    <button className="icon-btn" aria-label={`Move ${sec.name || 'section'} up`} disabled={i === 0} onClick={() => moveSection(i, -1)}><Icon name="chevron" size={14} className="rot-up" /></button>
                    <button className="icon-btn" aria-label={`Move ${sec.name || 'section'} down`} disabled={i === sections.length - 1} onClick={() => moveSection(i, 1)}><Icon name="down" size={14} /></button>
                    <button className="icon-btn" aria-label={`Rename ${sec.name || 'section'}`} onClick={() => renameSection(sec.id)}><Icon name="edit" size={14} /></button>
                    <button className="icon-btn" aria-label={`Remove ${sec.name || 'section'}`} disabled={sections.length < 2} onClick={() => removeSection(sec.id)}><Icon name="trash" size={14} /></button>
                  </span>}
                </div>
              )}
              <div className={`widget-grid ${editing ? 'editing' : ''}`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {inSec.map(card)}
                {editing && !inSec.length && <div {...sort.item(`section:${sec.id}`, 'rules-drop-hint')} style={{ gridColumn: '1 / -1' }}>Drag widgets here</div>}
              </div>
            </section>
          );
        })}
        {editing && <div><Button variant="ghost" icon="plus" onClick={addSection}>Add section</Button></div>}
      </div>
      {adding && (
        <Modal title={tx('Add a widget')} wide onClose={() => setAdding(null)}>
          <div className="widget-catalog">
            {[...sources.values()].map((s) => (
              <div key={s.id} className="widget-catalog-tool">
                <div className="widget-catalog-head"><Icon name={s.icon} size={16} /><strong>{s.name}</strong></div>
                {s.widgets.map((w) => {
                  const added = list.some((x) => x.toolId === s.id && x.widgetId === w.id);
                  const disabled = added && !w.multiple;
                  return (
                    <button key={w.id} className="widget-option" disabled={disabled} onClick={() => {
                      persist([...list, { key: newWidgetKey(), toolId: s.id, widgetId: w.id, ...(secs.length ? { section: adding } : {}) }]);
                      toast(`Added ${w.title}`);
                    }}>
                      <div><strong>{w.title}</strong><span className="muted small">{w.description}</span></div>
                      <span className="widget-option-size">{WIDGET_SIZE_LABEL[w.size]}</span>
                      {disabled ? <span className="badge badge-green">added</span> : <Icon name="plus" size={16} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </section>
  );
}
